import { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Upload, X, FileText, ChevronDown, ChevronUp, Check, AlertCircle,
  Loader2, Sparkles, Trash2, ArrowRight, RefreshCw
} from 'lucide-react'
import {
  PageHeader, Card, CardBody, Button, FormField, FormSelect,
  FormTextarea, ErrorMessage, Badge
} from '../components/common'
import { knowledgeBaseApi } from '../services/api'
import { getApiBaseUrl } from '../utils/config.js'
import { useAuth } from '../contexts/AuthContext'

const categoryOptions = [
  { value: '', label: 'Select category...' },
  { value: 'RulesAndRegulations', label: 'Rules & Regulations' },
  { value: 'StrataDocuments', label: 'Strata Documents' },
  { value: 'Financial', label: 'Financial' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'MeetingMinutes', label: 'Meeting Minutes' },
  { value: 'Maintenance', label: 'Maintenance' },
  { value: 'General', label: 'General' },
]

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.txt,.rtf,.csv,.xls,.xlsx'
const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB
const MAX_FILES = 20
const UPLOAD_CONCURRENCY = 3

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-slate-100 text-slate-600', icon: null },
  analyzing: { label: 'Analyzing...', color: 'bg-amber-100 text-amber-700', icon: Loader2 },
  ready: { label: 'Ready', color: 'bg-blue-100 text-blue-700', icon: Check },
  uploading: { label: 'Uploading...', color: 'bg-primary-100 text-primary-700', icon: Loader2 },
  uploaded: { label: 'Uploaded', color: 'bg-green-100 text-green-700', icon: Check },
  error: { label: 'Error', color: 'bg-red-100 text-red-700', icon: AlertCircle },
}

function StatusBadge({ status }) {
  const config = statusConfig[status] || statusConfig.pending
  const Icon = config.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
      {Icon && <Icon size={12} className={status === 'analyzing' || status === 'uploading' ? 'animate-spin' : ''} />}
      {config.label}
    </span>
  )
}

function getCategoryLabel(value) {
  const opt = categoryOptions.find(o => o.value === value)
  return opt ? opt.label : value
}

let nextFileId = 0

export default function KnowledgeDocumentUpload() {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const [files, setFiles] = useState([])
  const [dragActive, setDragActive] = useState(false)
  const [error, setError] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadComplete, setUploadComplete] = useState(false)

  const { selectedBuildingId: buildingId } = useAuth()

  // Navigation guard while uploading
  useEffect(() => {
    const handler = (e) => {
      if (isUploading) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isUploading])

  const validateFiles = useCallback((newFiles) => {
    const errors = []
    const valid = []
    const currentCount = files.length

    for (const file of newFiles) {
      if (currentCount + valid.length >= MAX_FILES) {
        errors.push(`"${file.name}" skipped — maximum ${MAX_FILES} files allowed`)
        continue
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${file.name}" exceeds 50MB limit`)
        continue
      }
      if (files.some(f => f.file.name === file.name && f.file.size === file.size)) {
        errors.push(`"${file.name}" is already in the queue`)
        continue
      }
      valid.push(file)
    }

    if (errors.length > 0) {
      setError(errors.join('. '))
    }

    return valid
  }, [files])

  const runBatchAnalysis = useCallback(async (fileEntries) => {
    const entriesToAnalyze = fileEntries.filter(f => f.status === 'pending')
    if (entriesToAnalyze.length === 0) return

    // Mark as analyzing
    setFiles(prev => prev.map(f =>
      entriesToAnalyze.some(e => e.id === f.id) ? { ...f, status: 'analyzing' } : f
    ))

    try {
      const metadata = entriesToAnalyze.map(f => ({
        fileName: f.file.name,
        contentType: f.file.type || 'application/octet-stream',
        fileSize: f.file.size,
      }))

      const response = await knowledgeBaseApi.batchAnalyzeDocuments(metadata)
      const results = response.data.results || []

      setFiles(prev => prev.map(f => {
        const idx = entriesToAnalyze.findIndex(e => e.id === f.id)
        if (idx === -1) return f
        const analysis = results[idx]
        if (!analysis) return { ...f, status: 'ready' }
        return {
          ...f,
          status: 'ready',
          analysis,
          formData: {
            title: analysis.title || '',
            description: analysis.description || '',
            category: analysis.category || '',
            tags: (analysis.tags || []).join(', '),
          },
        }
      }))
    } catch (err) {
      setFiles(prev => prev.map(f =>
        entriesToAnalyze.some(e => e.id === f.id)
          ? { ...f, status: 'ready', error: 'AI analysis failed — fill in manually' }
          : f
      ))
    }
  }, [])

  const addFiles = useCallback((rawFiles) => {
    const validated = validateFiles(Array.from(rawFiles))
    if (validated.length === 0) return

    const newEntries = validated.map(file => ({
      id: nextFileId++,
      file,
      status: 'pending',
      analysis: null,
      formData: { title: '', description: '', category: '', tags: '' },
      error: null,
      expanded: false,
    }))

    setFiles(prev => [...prev, ...newEntries])
    setUploadComplete(false)

    // Auto-trigger batch analysis
    runBatchAnalysis(newEntries)
  }, [validateFiles, runBatchAnalysis])

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(e.type === 'dragenter' || e.type === 'dragover')
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files?.length > 0) {
      addFiles(e.dataTransfer.files)
    }
  }

  const handleFileSelect = (e) => {
    if (e.target.files?.length > 0) {
      addFiles(e.target.files)
      e.target.value = '' // reset so same files can be re-selected
    }
  }

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  const clearAll = () => {
    setFiles([])
    setUploadComplete(false)
    setError(null)
  }

  const toggleExpanded = (id) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, expanded: !f.expanded } : f))
  }

  const updateFormData = (id, field, value) => {
    setFiles(prev => prev.map(f =>
      f.id === id ? { ...f, formData: { ...f.formData, [field]: value } } : f
    ))
  }

  const uploadSingleFile = async (entry) => {
    const fd = new FormData()
    fd.append('File', entry.file)
    fd.append('BuildingId', buildingId)
    if (entry.formData.title) fd.append('Title', entry.formData.title)
    if (entry.formData.description) fd.append('Description', entry.formData.description)
    if (entry.formData.category) fd.append('Category', entry.formData.category)
    if (entry.formData.tags) {
      const tagsArray = entry.formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      fd.append('TagsJson', JSON.stringify(tagsArray))
    }

    const token = localStorage.getItem('authToken')
    const response = await fetch(`${getApiBaseUrl()}/api/admin/KnowledgeBase/documents/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: fd,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      throw new Error(errorData?.message || errorData?.error || `Upload failed (${response.status})`)
    }

    return await response.json()
  }

  const handleUploadAll = async () => {
    const toUpload = files.filter(f => f.status === 'ready' || f.status === 'error')
    if (toUpload.length === 0) return

    setIsUploading(true)
    setError(null)

    // Mark all as uploading
    setFiles(prev => prev.map(f =>
      toUpload.some(u => u.id === f.id) ? { ...f, status: 'uploading', error: null } : f
    ))

    // Upload with concurrency limit
    const queue = [...toUpload]
    const uploadNext = async () => {
      while (queue.length > 0) {
        const entry = queue.shift()
        try {
          await uploadSingleFile(entry)
          setFiles(prev => prev.map(f => f.id === entry.id ? { ...f, status: 'uploaded', error: null } : f))
        } catch (err) {
          setFiles(prev => prev.map(f =>
            f.id === entry.id ? { ...f, status: 'error', error: err.message } : f
          ))
        }
      }
    }

    const workers = Array.from({ length: Math.min(UPLOAD_CONCURRENCY, toUpload.length) }, () => uploadNext())
    await Promise.all(workers)

    setIsUploading(false)
    setFiles(prev => {
      const allDone = prev.every(f => f.status === 'uploaded')
      if (allDone) setUploadComplete(true)
      return prev
    })
  }

  const retryFile = async (id) => {
    const entry = files.find(f => f.id === id)
    if (!entry) return

    setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'uploading', error: null } : f))

    try {
      await uploadSingleFile(entry)
      setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'uploaded', error: null } : f))
    } catch (err) {
      setFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'error', error: err.message } : f))
    }
  }

  const readyCount = files.filter(f => f.status === 'ready' || f.status === 'error').length
  const uploadedCount = files.filter(f => f.status === 'uploaded').length
  const errorCount = files.filter(f => f.status === 'error').length
  const analyzingCount = files.filter(f => f.status === 'analyzing').length
  const hasFiles = files.length > 0

  return (
    <div className="space-y-6">
      <PageHeader
        title="Upload Documents"
        subtitle={`Upload up to ${MAX_FILES} documents to your building's knowledge base`}
        backLink={{ label: 'Back to Documents', href: '/knowledge-base/documents' }}
      />

      {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

      {/* Upload complete banner */}
      {uploadComplete && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <Check size={18} className="text-green-600" />
            </div>
            <div>
              <p className="font-medium text-green-800">All {uploadedCount} documents uploaded successfully!</p>
              <p className="text-sm text-green-600">Documents are ready for knowledge base sync.</p>
            </div>
          </div>
          <Button variant="primary" size="small" onClick={() => navigate('/knowledge-base/documents')}>
            Go to Documents <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
      )}

      {/* Drop zone */}
      <Card>
        <CardBody>
          <div
            className={`border-2 border-dashed rounded-xl text-center transition-colors cursor-pointer
              ${hasFiles ? 'p-4' : 'p-8'}
              ${dragActive ? 'border-primary-500 bg-primary-50' : 'border-slate-300 hover:border-slate-400'}
              ${files.length >= MAX_FILES ? 'opacity-50 cursor-not-allowed' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={files.length >= MAX_FILES ? (e) => { e.preventDefault(); e.stopPropagation() } : handleDrop}
            onClick={() => files.length < MAX_FILES && fileInputRef.current?.click()}
          >
            <Upload size={hasFiles ? 20 : 32} className="mx-auto text-slate-400 mb-2" />
            {hasFiles ? (
              <p className="text-sm text-slate-600">Drop more files here or click to add (max {MAX_FILES})</p>
            ) : (
              <>
                <p className="text-slate-600 mb-1">Drop your files here, or click to browse</p>
                <p className="text-xs text-slate-400">PDF, DOCX, TXT, XLS, CSV &mdash; up to 50MB each, {MAX_FILES} files max</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept={ACCEPTED_TYPES}
            />
          </div>
        </CardBody>
      </Card>

      {/* File queue */}
      {hasFiles && (
        <Card>
          <CardBody className="p-0">
            {/* Queue header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-slate-900">{files.length} file{files.length !== 1 ? 's' : ''}</h3>
                {analyzingCount > 0 && (
                  <span className="text-sm text-amber-600 flex items-center gap-1">
                    <Sparkles size={14} /> Analyzing {analyzingCount} file{analyzingCount !== 1 ? 's' : ''}...
                  </span>
                )}
                {uploadedCount > 0 && (
                  <span className="text-sm text-green-600">{uploadedCount} uploaded</span>
                )}
                {errorCount > 0 && (
                  <span className="text-sm text-red-600">{errorCount} failed</span>
                )}
              </div>
              <button
                onClick={clearAll}
                disabled={isUploading}
                className="text-sm text-slate-500 hover:text-red-600 disabled:opacity-50"
              >
                Clear All
              </button>
            </div>

            {/* File rows */}
            <div className="divide-y divide-slate-100">
              {files.map(entry => (
                <div key={entry.id} className="group">
                  {/* Collapsed row */}
                  <div
                    className={`flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-slate-50 transition-colors
                      ${entry.status === 'uploaded' ? 'bg-green-50/50' : ''}
                      ${entry.status === 'error' ? 'bg-red-50/50' : ''}`}
                    onClick={() => entry.status !== 'uploading' && toggleExpanded(entry.id)}
                  >
                    <FileText size={18} className={
                      entry.status === 'uploaded' ? 'text-green-500' :
                      entry.status === 'error' ? 'text-red-500' :
                      'text-primary-500'
                    } />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-900 truncate">{entry.file.name}</p>
                        <span className="text-xs text-slate-400 flex-shrink-0">{formatFileSize(entry.file.size)}</span>
                      </div>
                      {entry.formData.title && entry.status !== 'pending' && entry.status !== 'analyzing' && (
                        <p className="text-xs text-slate-500 truncate mt-0.5">{entry.formData.title}</p>
                      )}
                      {entry.error && (
                        <p className="text-xs text-red-600 mt-0.5">{entry.error}</p>
                      )}
                    </div>

                    {entry.formData.category && entry.status !== 'pending' && entry.status !== 'analyzing' && (
                      <Badge variant="default">{getCategoryLabel(entry.formData.category)}</Badge>
                    )}

                    <StatusBadge status={entry.status} />

                    {entry.status === 'error' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); retryFile(entry.id) }}
                        className="p-1 text-slate-400 hover:text-primary-600"
                        title="Retry upload"
                      >
                        <RefreshCw size={14} />
                      </button>
                    )}

                    {entry.status !== 'uploading' && entry.status !== 'uploaded' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); removeFile(entry.id) }}
                        className="p-1 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove"
                      >
                        <X size={14} />
                      </button>
                    )}

                    <button
                      className="p-1 text-slate-400"
                      onClick={(e) => { e.stopPropagation(); toggleExpanded(entry.id) }}
                    >
                      {entry.expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  </div>

                  {/* Expanded form */}
                  {entry.expanded && (
                    <div className="px-5 pb-4 pt-1 bg-slate-50/50 space-y-3 border-t border-slate-100">
                      {entry.analysis && (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Sparkles size={12} className="text-amber-500" />
                          AI confidence: {(entry.analysis.confidence * 100).toFixed(0)}%
                          {entry.analysis.tags?.length > 0 && (
                            <span className="flex items-center gap-1 ml-2">
                              Suggested tags: {entry.analysis.tags.map(t => (
                                <Badge key={t} variant="default">{t}</Badge>
                              ))}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-3">
                        <FormField
                          label="Title"
                          value={entry.formData.title}
                          onChange={(e) => updateFormData(entry.id, 'title', e.target.value)}
                          placeholder="Document title"
                          disabled={entry.status === 'uploading' || entry.status === 'uploaded'}
                        />
                        <FormSelect
                          label="Category"
                          value={entry.formData.category}
                          onChange={(e) => updateFormData(entry.id, 'category', e.target.value)}
                          options={categoryOptions}
                          disabled={entry.status === 'uploading' || entry.status === 'uploaded'}
                        />
                      </div>

                      <FormTextarea
                        label="Description"
                        value={entry.formData.description}
                        onChange={(e) => updateFormData(entry.id, 'description', e.target.value)}
                        placeholder="Brief description"
                        disabled={entry.status === 'uploading' || entry.status === 'uploaded'}
                        rows={2}
                      />

                      <FormField
                        label="Tags (comma-separated)"
                        value={entry.formData.tags}
                        onChange={(e) => updateFormData(entry.id, 'tags', e.target.value)}
                        placeholder="e.g. bylaws, parking, noise"
                        disabled={entry.status === 'uploading' || entry.status === 'uploaded'}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Actions bar */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50">
              <div className="text-sm text-slate-500">
                {readyCount > 0 && `${readyCount} file${readyCount !== 1 ? 's' : ''} ready to upload`}
                {analyzingCount > 0 && readyCount > 0 && ' · '}
                {analyzingCount > 0 && `${analyzingCount} analyzing`}
              </div>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => navigate('/knowledge-base/documents')}
                  disabled={isUploading}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleUploadAll}
                  disabled={readyCount === 0 || isUploading || !buildingId || analyzingCount > 0}
                  loading={isUploading}
                >
                  Upload {readyCount > 0 ? `(${readyCount})` : 'All'}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}
